import React from 'react';
import { Task } from '../types';
import { isOverdue, formatDate } from './helpers';
import styles from '../../../../styles/TaskManagement.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';

interface TaskCardProps {
  task: Task;
  onClick: (task: Task) => void;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  showEditDelete?: boolean;
  isWorkMode?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onClick, onEdit, onDelete, showEditDelete = false, isWorkMode = false }) => {
  const description = task.description || 'No description provided';

  const formatPriority = (priority: string) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const getPriorityClass = (priority: string) => {
    const formatted = formatPriority(priority);
    return `priority${formatted}`;
  };

  const formatStatus = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusClass = (status: string) => {
    return `status${status
      .split('_')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('')}`;
  };

  const getDaysUntilDue = (dueDate: string): string => {
    if (!dueDate) return '';
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const statusClass = getStatusClass(task.status);
  const priorityClass = getPriorityClass(task.priority);

  const category = task.category || {};
  const categoryColor = category.color || '#6B7280';
  const categoryIcon = category.icon || 'folder';
  const categoryName = category.name || 'Uncategorized';

  return (
    <div className={styles.taskCard} onClick={() => onClick(task)}>
      {/* Card Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div
            className={styles.taskIconWrapper}
            style={{
              backgroundColor: task.is_proposal ? '#EFF6FF' : `${categoryColor}20`,
              color: task.is_proposal ? '#3B82F6' : undefined
            }}
          >
            {task.is_proposal ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
            ) : (
              <i
                className={`bi bi-${categoryIcon}`}
                style={{ color: categoryColor, fontSize: '1.5rem' }}
              ></i>
            )}
          </div>
          <div className={styles.cardTitleSection}>
            <h3 className={styles.cardTitle}>{task.name}</h3>
            <div className={styles.cardDescription}>
              {description}
            </div>
          </div>
        </div>
        <div className={styles.cardHeaderRight}>
          {!(task.allow_user_instances && !task.is_instance) && (
            <div className={`${styles.taskStatusBadge} ${styles[statusClass]}`}>
              {formatStatus(task.status)}
            </div>
          )}
          {showEditDelete && (onEdit || onDelete) && (
            <div className={styles.cardActions}>
              {onEdit && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}
                  title="Edit Task"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              )}
              {onDelete && (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnDanger} ${buttonStyles.btnIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(task);
                  }}
                  title="Delete Task"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3,6 5,6 21,6"></polyline>
                    <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Card Body - Tags and Badges */}
      <div className={styles.cardBody}>
        <div className={styles.cardTags}>
          {/* Proposal-specific Badges */}
          {task.is_proposal ? (
            <>
              {/* Request Badge */}
              <div
                className={styles.categoryTag}
                style={{
                  backgroundColor: '#eff6ff',
                  color: '#3b82f6',
                  border: '1px solid #dbeafe'
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                </svg>
                <span style={{ fontSize: '0.625rem', fontWeight: '500' }}>Request</span>
              </div>

              {/* Stage Badge */}
              {task.current_stage && (
                <div
                  className={styles.categoryTag}
                  style={{
                    backgroundColor: '#dcfce7',
                    color: '#15803d',
                    border: '1px solid #bbf7d0',
                    textTransform: 'uppercase'
                  }}
                >
                  <span style={{ fontSize: '0.625rem', fontWeight: '600' }}>Stage: {task.current_stage.replace(/_/g, ' ')}</span>
                </div>
              )}

              {/* Priority Badge */}
              <div
                className={styles.categoryTag}
                style={{
                  backgroundColor: '#fff7ed',
                  color: '#c2410c',
                  border: '1px solid #fdba74',
                  textTransform: 'uppercase'
                }}
              >
                <span style={{ fontSize: '0.625rem', fontWeight: '600' }}>{task.priority || 'Medium'} Priority</span>
              </div>
            </>
          ) : (
            // Regular Task Badges
            <>
              <div
                className={styles.categoryTag}
                style={{
                  backgroundColor: `${categoryColor}15`,
                  color: categoryColor,
                  borderColor: `${categoryColor}40`
                }}
              >
                <i className={`bi bi-${categoryIcon}`} style={{ marginRight: '0.375rem', fontSize: '0.75rem' }}></i>
                <span style={{ fontSize: '0.625rem' }}>{categoryName}</span>
              </div>

              {/* Template Badge */}
              {task.allow_user_instances && !task.is_instance && (
                <div
                  className={styles.categoryTag}
                  style={{
                    backgroundColor: '#fbbf2415',
                    color: '#f59e0b',
                    borderColor: '#fbbf2440'
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                  <span style={{ fontSize: '0.625rem', fontWeight: '500' }}>Template</span>
                </div>
              )}

              {/* Instance Badge */}
              {task.is_instance && (
                <div
                  className={styles.categoryTag}
                  style={{
                    backgroundColor: '#3b82f615',
                    color: '#2563eb',
                    borderColor: '#3b82f640'
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                  <span style={{ fontSize: '0.625rem', fontWeight: '500' }}>Instance</span>
                </div>
              )}

              {/* Priority Tag */}
              <div className={`${styles.priorityTag} ${styles[priorityClass]}`}>
                <span style={{ fontSize: '0.625rem' }}>{formatPriority(task.priority)} Priority</span>
              </div>
            </>
          )}

          {/* Due Date Tag (Common) */}
          {!task.is_proposal && task.due_date && (
            <div className={`${styles.deadlineTag} ${isOverdue(task.due_date) ? styles.overdue : ''}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span style={{ fontSize: '0.625rem' }}>{getDaysUntilDue(task.due_date)}</span>
            </div>
          )}
          {/* Group Tag - Only show if group exists */}
          {task.group && task.group.name && (
            <div className={styles.groupTag}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <span style={{ fontSize: '0.625rem' }}>{task.group.name}</span>
            </div>
          )}

          {/* Points Tag - Hide for proposals */}
          {!task.is_proposal && task.points_earned !== undefined && task.points_earned > 0 && (
            <div className={styles.pointsTag}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span style={{ fontSize: '0.625rem' }}>{task.points_earned} Points</span>
            </div>
          )}
        </div>


        {/* Metadata Section */}
        <div className={styles.cardMetadata}>
          {task.is_proposal ? (
            // Proposal Metadata
            <>
              <div className={styles.metadataItem}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span className={styles.metadataText}>Submitted: {formatDate(task.created_at)}</span>
              </div>
              <div className={styles.metadataItem}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span className={styles.metadataText}>Requested by: {task.created_by.full_name}</span>
              </div>
            </>
          ) : (
            // Standard Task Metadata
            <>
              {task.requires_approval && (
                <div className={styles.metadataItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <span className={styles.metadataText}>Requires Approval</span>
                </div>
              )}
              {task.requires_documents && (
                <div className={styles.metadataItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                  <span className={styles.metadataText}>Requires Documents</span>
                </div>
              )}
              {task.assignees && task.assignees.length > 0 && (
                <div className={styles.metadataItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <span className={styles.metadataText}>
                    {task.assignees.length} Assignee{task.assignees.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <div className={styles.metadataItem}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <span className={styles.metadataText}>Created: {formatDate(task.created_at)}</span>
              </div>
              {task.updated_at !== task.created_at && (
                <div className={styles.metadataItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span className={styles.metadataText}>Updated: {formatDate(task.updated_at)}</span>
                </div>
              )}
              {task.created_by && !isWorkMode && (
                <div className={styles.metadataItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span className={styles.metadataText}>By: {task.created_by.full_name}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskCard;

